import React, { useState } from 'react';
import { clsx } from 'clsx';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  render?: (value: unknown, row: T) => React.ReactNode;
  cell?: (params: { row: { original: T }; value: unknown }) => React.ReactNode;
  className?: string;
}

export interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  variant?: 'default' | 'zebra' | 'compact';
  selectable?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function Table<T extends Record<string, unknown>>({
  data,
  columns,
  keyField,
  variant = 'default',
  selectable = false,
  onSelectionChange,
  loading = false,
  emptyMessage = 'No data available',
  className,
}: TableProps<T>) {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedRows, setSelectedRows] = useState<Set<T[keyof T]>>(new Set());

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Handle comparison of unknown types safely
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      // Fallback to string comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [data, sortField, sortDirection]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allKeys = new Set(data.map((row) => row[keyField]));
      setSelectedRows(allKeys);
      onSelectionChange?.(data);
    } else {
      setSelectedRows(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (row: T) => {
    const key = row[keyField];
    const newSelected = new Set(selectedRows);

    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }

    setSelectedRows(newSelected);
    const selectedData = data.filter((r) => newSelected.has(r[keyField]));
    onSelectionChange?.(selectedData);
  };

  const tableClasses = clsx(
    'table',
    variant === 'zebra' && 'table-zebra',
    variant === 'compact' && 'table-compact',
    className
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className={tableClasses}>
        <thead>
          <tr>
            {selectable && (
              <th>
                <label>
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={selectedRows.size === data.length && data.length > 0}
                    onChange={handleSelectAll}
                  />
                </label>
              </th>
            )}
            {columns.map((column) => (
              <th
                key={column.key.toString()}
                className={clsx(column.sortable && 'cursor-pointer select-none', column.className)}
                onClick={() => column.sortable && handleSort(column.key.toString())}
              >
                <div className="flex items-center gap-2">
                  {column.header}
                  {column.sortable && sortField === column.key && (
                    <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (selectable ? 1 : 0)} className="text-center">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedData.map((row) => (
              <tr key={String(row[keyField])}>
                {selectable && (
                  <td>
                    <label>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedRows.has(row[keyField])}
                        onChange={() => handleSelectRow(row)}
                      />
                    </label>
                  </td>
                )}
                {columns.map((column) => (
                  <td key={column.key.toString()} className={column.className}>
                    {column.cell
                      ? column.cell({ row: { original: row }, value: row[column.key] })
                      : column.render
                        ? column.render(row[column.key], row)
                        : String(row[column.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
